import { create } from 'zustand';
import type { ErpEnvironment } from '@/shared/erp';
import { extranetAuthService } from '../api/authService';
import { sessionPersonOf, sessionService } from '../api/sessionService';
import { formatPersonName } from '../utils/format';
import type {
  ErpSession,
  ExtranetAuthState,
  ExtranetLoginRequest,
  ExtranetLoginResponse,
  ExtranetPerson,
  UserTenant,
} from './types';

/**
 * `localStorage` keys of the extranet session (contract §9). The spoof trio
 * read by `addSpoofInterceptor(extranetApiClient, 'extranet',
 * 'extranet_session_id')` is `SPOOF_TENANT`, `SPOOF_BEARER` and `SESSION_ID`.
 */
export const EXTRANET_STORAGE_KEYS = {
  token: 'extranet_auth_token',
  tenant: 'extranet_active_tenant_id',
  registrySlug: 'extranet_registry_slug',
  sessionId: 'extranet_session_id',
  sessionLabel: 'extranet_session_label',
  canSwitchSession: 'extranet_can_switch_session',
  person: 'extranet_person',
  availableSessions: 'extranet_available_sessions',
  // Additions to contract §9 — needed to restore the header / banner / rule
  // context after a reload.
  sessionPerson: 'extranet_session_person',
  environment: 'extranet_environment',
  isProduction: 'extranet_is_production',
  spoofTenant: 'extranet_spoofed_tenant_id',
  spoofBearer: 'extranet_spoofed_bearer_token',
} as const;

const KEYS = EXTRANET_STORAGE_KEYS;

// ---------------------------------------------------------------------------
// localStorage readers (defensive: a corrupt value never breaks the boot)
// ---------------------------------------------------------------------------

function readJson<T>(key: string, fallback: T): T {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function readNumber(key: string): number | null {
  const raw = localStorage.getItem(key);
  if (raw === null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function readFlag(key: string): boolean {
  return localStorage.getItem(key) === '1';
}

function writeOrRemove(key: string, value: string | null): void {
  if (value === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, value);
  }
}

/** Label of a session person (`Prénom Nom`, else the session id). */
export function sessionLabelOf(person: ExtranetPerson | null, sessionId: number): string {
  const name = person ? formatPersonName(person.prenom, person.nom) : '';
  return name === '' ? String(sessionId) : name;
}

/**
 * Active tenant id after login: the tenant whose slug is in the URL, else
 * the persisted one, else the user's first tenant.
 */
function resolveActiveTenantId(tenants: UserTenant[], tenantSlug?: string): string | null {
  const fromSlug = tenantSlug ? tenants.find((t) => t.slug === tenantSlug) : undefined;
  if (fromSlug) return String(fromSlug.id);
  const persisted = localStorage.getItem(KEYS.tenant);
  if (persisted) return persisted;
  const first = tenants[0];
  return first ? String(first.id) : null;
}

/** State slice reset on logout. */
function signedOutState() {
  return {
    token: null,
    user: null,
    tenants: [],
    activeTenantId: null,
    isAuthenticated: false,
    registrySlug: null,
    environment: null,
    isProduction: false,
    sessionId: null,
    sessionLabel: null,
    canSwitchSession: false,
    person: null,
    sessionPerson: null,
    availableSessions: [],
    spoofedTenantId: null,
    spoofedBearerToken: null,
  } satisfies Partial<ExtranetAuthState>;
}

export const useExtranetAuthStore = create<ExtranetAuthState>((set, get) => ({
  token: localStorage.getItem(KEYS.token),
  user: null,
  tenants: [],
  activeTenantId: localStorage.getItem(KEYS.tenant),
  isAuthenticated: !!localStorage.getItem(KEYS.token),
  isLoading: false,
  error: null,

  registrySlug: localStorage.getItem(KEYS.registrySlug),
  environment: localStorage.getItem(KEYS.environment) as ErpEnvironment | null,
  isProduction: readFlag(KEYS.isProduction),
  sessionId: readNumber(KEYS.sessionId),
  sessionLabel: localStorage.getItem(KEYS.sessionLabel),
  canSwitchSession: readFlag(KEYS.canSwitchSession),
  person: readJson<ExtranetPerson | null>(KEYS.person, null),
  sessionPerson: readJson<ExtranetPerson | null>(KEYS.sessionPerson, null),
  availableSessions: readJson<ErpSession[]>(KEYS.availableSessions, []),

  spoofedTenantId: localStorage.getItem(KEYS.spoofTenant),
  spoofedBearerToken: localStorage.getItem(KEYS.spoofBearer),

  beginSession: (data: ExtranetLoginResponse['data'], tenantSlug?: string) => {
    const { token, user, erp } = data;
    const tenants = user.tenants ?? [];
    const activeTenantId = resolveActiveTenantId(tenants, tenantSlug);
    const sessionLabel = sessionLabelOf(erp.session_person, erp.session_id);

    localStorage.setItem(KEYS.token, token);
    writeOrRemove(KEYS.tenant, activeTenantId);
    localStorage.setItem(KEYS.registrySlug, erp.registry_slug);
    writeOrRemove(KEYS.environment, erp.environment);
    localStorage.setItem(KEYS.isProduction, erp.is_production ? '1' : '0');
    localStorage.setItem(KEYS.sessionId, String(erp.session_id));
    localStorage.setItem(KEYS.sessionLabel, sessionLabel);
    localStorage.setItem(KEYS.canSwitchSession, erp.can_switch_session ? '1' : '0');
    localStorage.setItem(KEYS.person, JSON.stringify(erp.person));
    localStorage.setItem(KEYS.sessionPerson, JSON.stringify(erp.session_person));
    // A new login never inherits the previous user's session list.
    localStorage.removeItem(KEYS.availableSessions);

    set({
      token,
      user,
      tenants,
      activeTenantId,
      registrySlug: erp.registry_slug,
      environment: erp.environment,
      isProduction: erp.is_production,
      sessionId: erp.session_id,
      sessionLabel,
      canSwitchSession: erp.can_switch_session,
      person: erp.person,
      sessionPerson: erp.session_person,
      availableSessions: [],
      error: null,
    });
  },

  completeLogin: () => {
    set({ isAuthenticated: true, isLoading: false, error: null });
  },

  login: async (credentials: ExtranetLoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await extranetAuthService.login(credentials);
      get().beginSession(response.data, credentials.tenant_slug);
      get().completeLogin();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Login failed';
      const apiMessage =
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message ?? message;
      set({ error: apiMessage, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await extranetAuthService.logout();
    } catch {
      // Ignore logout API errors - clear local state regardless
    } finally {
      for (const key of Object.values(KEYS)) {
        localStorage.removeItem(key);
      }
      set(signedOutState());
    }
  },

  checkAuth: () => {
    const token = localStorage.getItem(KEYS.token);
    set({ isAuthenticated: !!token, token });
  },

  clearError: () => {
    set({ error: null });
  },

  setActiveTenant: (tenantId: string) => {
    localStorage.setItem(KEYS.tenant, tenantId);
    set({ activeTenantId: tenantId });
  },

  fetchUser: async () => {
    try {
      const response = await extranetAuthService.me();
      const user = response.data;
      const tenants = user.tenants ?? [];
      const activeTenantId = resolveActiveTenantId(tenants);
      writeOrRemove(KEYS.tenant, activeTenantId);
      set({ user, tenants, activeTenantId });
    } catch {
      // Ignore fetch errors - user will remain null
    }
  },

  setAvailableSessions: (sessions: ErpSession[]) => {
    localStorage.setItem(KEYS.availableSessions, JSON.stringify(sessions));
    set({ availableSessions: sessions });
  },

  switchSession: async (sessionId, label, options) => {
    const reload = options?.reload ?? true;
    // Persist first: the spoof interceptor reads the session id from
    // localStorage, so the DEF_LOGIN call below already runs under it.
    localStorage.setItem(KEYS.sessionId, String(sessionId));
    localStorage.setItem(KEYS.sessionLabel, label);
    set({ sessionId, sessionLabel: label });

    try {
      const row = await sessionService.fetchDefLogin(get().registrySlug);
      if (row) {
        const sessionPerson = sessionPersonOf(row);
        localStorage.setItem(KEYS.sessionPerson, JSON.stringify(sessionPerson));
        set({ sessionPerson });
      }
    } catch {
      // The switch itself is done (SES persisted); a failed refresh of the
      // session person must not block the user.
    } finally {
      if (reload) {
        globalThis.location.reload();
      }
    }
  },

  setSpoofedTenantId: (tenantId: string | null) => {
    writeOrRemove(KEYS.spoofTenant, tenantId);
    set({ spoofedTenantId: tenantId });
  },

  setSpoofedBearerToken: (token: string | null) => {
    writeOrRemove(KEYS.spoofBearer, token);
    set({ spoofedBearerToken: token });
  },

  clearSpoofing: () => {
    localStorage.removeItem(KEYS.spoofTenant);
    localStorage.removeItem(KEYS.spoofBearer);
    set({ spoofedTenantId: null, spoofedBearerToken: null });
  },
}));

/**
 * `true` when a SUPERVUE user works on another session than their own
 * (the active `SES` differs from their `ID_PER`).
 */
export function isSpoofedSession(
  sessionId: number | null,
  person: ExtranetPerson | null,
): boolean {
  return sessionId !== null && person !== null && sessionId !== person.id;
}
